/**
 * Which tabs a competition's shell offers (specs/champions-league AC-11.8).
 *
 * The Leaderboard tab is earned the way the header's Table button is: a global board of four
 * names ranks nobody and invites a comparison the competition cannot yet support, while the
 * squad board does that job from the first member. So below the threshold the tab is not
 * rendered at all, rather than rendered over a "coming soon" pane.
 *
 * Pure — no React, no queries, so both the gate and the redirect are testable on their own.
 */

import type { CompetitionTab } from './competitionRoutes.helper';
import { LEADERBOARD_DEFAULT_MIN_SHOW_THRESHOLD } from './competitionLeaderboard.helper';

/** The full set, in the order the tab bar draws them. */
const ALL_TABS: readonly CompetitionTab[] = ['picks', 'leaderboard', 'groups'];

/**
 * Whether a competition's global leaderboard is worth offering as a tab.
 *
 * `undefined` means the server did not send a count — a build older than the field — and must
 * read as "offer it", which is what every client did before this existed. Only a count that is
 * actually present and actually below the threshold hides the tab, so a deploy in either order
 * degrades to today's behaviour rather than to a missing tab.
 */
export function shouldOfferLeaderboardTab(
  participantCount: number | null | undefined,
  threshold: number = LEADERBOARD_DEFAULT_MIN_SHOW_THRESHOLD,
): boolean {
  if (typeof participantCount !== 'number' || !Number.isFinite(participantCount)) return true;
  return participantCount >= threshold;
}

/**
 * The tabs to render, in order. Picks and Squads are unconditional: a competition always has
 * fixtures to pick and a squad to join.
 */
export function competitionTabsFor(
  participantCount: number | null | undefined,
  threshold: number = LEADERBOARD_DEFAULT_MIN_SHOW_THRESHOLD,
): CompetitionTab[] {
  const offerLeaderboard = shouldOfferLeaderboardTab(participantCount, threshold);
  return ALL_TABS.filter((tab) => tab !== 'leaderboard' || offerLeaderboard);
}

/**
 * Where a viewer should be sent when the tab they are on is no longer offered, or null to stay
 * put. Without this, someone deep-linked to `/leaderboard` — or standing on it when the tab
 * disappears — is left on a route no tab owns, with the bar highlighting nothing.
 */
export function redirectTabWhenUnavailable(
  activeTab: CompetitionTab,
  tabs: readonly CompetitionTab[],
): CompetitionTab | null {
  // `knockout` is a World Cup route with no generic counterpart and is never in this list, so
  // it must not be treated as "unavailable" and bounced to picks.
  if (activeTab === 'knockout') return null;
  return tabs.includes(activeTab) ? null : 'picks';
}
